import { Node, useReactFlow } from "reactflow";
import { operationType } from "../../../services/types";
import { useSelector } from "react-redux";
import { RootState } from "../../../redux/store";
import { useSearchParams } from "react-router-dom";
import { webURL } from "../../../services/API";
import axios from "axios";
import styled, { keyframes, css } from "styled-components";
import React from "react";
import { Shield, MessageCircle, Trash2, Send, Terminal, Zap, Bot, User, RotateCcw, AlertTriangle, MessagesSquare, Activity, Layers, Radio, Mic, MicOff, PhoneOff, Volume2 } from "lucide-react";
import { updateNodeValue } from "../../../services/method";
import { UserChatsMonitor } from "./userChatsMonitor";
import { theme } from "../../../styles/theme";
import { getVoiceStatusLabel, useInteractiveVoiceAgent } from "../../../hooks/useInteractiveVoiceAgent";

export interface settingsDataType { isToggle: boolean }

type ChatMode = 'user' | 'admin' | 'monitor';

type AgentRunResponse = {
    message: string;
    intermediateSteps?: Array<{
        step: number;
        tool: string;
        input?: any;
        output?: any;
        status?: 'success' | 'error';
        error?: string;
    }>;
    thread_id?: string;
    workflowMeta?: {
        name: string;
        runsToday: number;
        totalRuns: number;
        lastRun: string | null;
        loopActive: boolean;
        loopInterval: number;
        nodeCount: number;
        triggerCount: number;
    };
};

// Chat Component with Admin/User tabs and per-user isolation
export function AIChatInterface({ nodeId, currentNode }: { nodeId: string, currentNode: Node<operationType> | undefined }) {
    const [chatMode, setChatMode] = React.useState<ChatMode>('monitor');
    const [userMessages, setUserMessages] = React.useState<Array<{ id?: string, role: string, content: string, timestamp: Date }>>([]);
    const [adminMessages, setAdminMessages] = React.useState<Array<{ id?: string, role: string, content: string, timestamp: Date }>>([]);
    const [inputMessage, setInputMessage] = React.useState("");
    const [isLoading, setIsLoading] = React.useState(false);
    const [searchParams] = useSearchParams();
    const projectId = searchParams.get('id');
    const userId = useSelector((state: RootState) => state?.user?.data?._id);
    const userToken = useSelector((state: RootState) => state.user.data?.token);
    const messagesEndRef = React.useRef<HTMLDivElement>(null);
    const hasLoadedUserHistory = React.useRef(false);
    const hasLoadedAdminHistory = React.useRef(false);
    const nodeIndexRef = React.useRef<string | null>(null);
    const [workflowMeta, setWorkflowMeta] = React.useState<AgentRunResponse['workflowMeta'] | null>(null);
    const [forcedInstruction, setForcedInstruction] = React.useState<string | null>(null);
    const [forceInput, setForceInput] = React.useState("");
    const [showForcePanel, setShowForcePanel] = React.useState(false);
    const [isClearingMemory, setIsClearingMemory] = React.useState(false);

    const { setNodes, getEdges, getNodes } = useReactFlow();

    const messages = chatMode === 'admin' ? adminMessages : userMessages;
    const setMessages = chatMode === 'admin' ? setAdminMessages : setUserMessages;

    const setMessagesForMode = React.useCallback((
        mode: ChatMode,
        updater: React.SetStateAction<Array<{ id?: string, role: string, content: string, timestamp: Date }>>
    ) => {
        if (mode === 'admin') setAdminMessages(updater);
        else setUserMessages(updater);
    }, []);

    // Per-user thread ID — always includes userId for isolation
    const getThreadId = React.useCallback((mode: ChatMode = chatMode) => {
        const nodeIndex = currentNode?.data?.index || nodeIndexRef.current;
        if (!projectId || !userId || !nodeIndex) return '';
        const prefix = mode === 'admin' ? 'admin' : 'user';
        return `${projectId}-${nodeIndex}-${prefix}-${userId}`;
    }, [projectId, userId, currentNode?.data?.index, chatMode]);

    // Cache node index on mount
    React.useEffect(() => {
        if (currentNode?.data?.index && !nodeIndexRef.current) {
            nodeIndexRef.current = currentNode.data.index;
        }
    }, []);

    // Scroll to bottom when messages change
    React.useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages]);

    // Load stats on mount for admin panel + poll every 30s
    React.useEffect(() => {
        if (projectId && userToken) {
            loadWorkflowStats();
            const interval = setInterval(loadWorkflowStats, 30000);
            return () => clearInterval(interval);
        }
    }, [projectId, userToken]);

    // Load chat history when component mounts or mode changes
    React.useEffect(() => {
        if (!projectId || !userId || !userToken || !nodeIndexRef.current) return;
        if (chatMode === 'user' && !hasLoadedUserHistory.current) {
            hasLoadedUserHistory.current = true;
            loadChatHistory('user');
        } else if (chatMode === 'admin' && !hasLoadedAdminHistory.current) {
            hasLoadedAdminHistory.current = true;
            loadChatHistory('admin');
        }
    }, [projectId, userId, userToken, chatMode]);

    // Monitor memory type changes
    React.useEffect(() => {
        if (currentNode?.data?.value?.memory === 'no-memory') {
            setUserMessages([]);
            setAdminMessages([]);
            hasLoadedUserHistory.current = false;
            hasLoadedAdminHistory.current = false;
        } else if (currentNode?.data?.value?.memory === 'simple-memory') {
            if (!hasLoadedUserHistory.current) {
                hasLoadedUserHistory.current = true;
                loadChatHistory('user');
            }
            if (!hasLoadedAdminHistory.current) {
                hasLoadedAdminHistory.current = true;
                loadChatHistory('admin');
            }
        }
    }, [currentNode?.data?.value?.memory]);

    const loadWorkflowStats = async () => {
        if (!projectId || !userToken) return;
        try {
            const response = await fetch(`${webURL}/agents/workflowStats?projectId=${projectId}`, {
                headers: { 'x-auth-token': userToken }
            });
            if (response.ok) {
                const stats = await response.json();
                setWorkflowMeta(stats);
            }
        } catch (error) {
            console.error('Error loading workflow stats:', error);
        }
    };

    const executeAiAgent = async (messageText: string, mode: ChatMode = chatMode) => {
        if (!userToken || !projectId) {
            throw new Error("Missing authentication");
        }

        const threadId = getThreadId(mode);
        const sentText = messageText.trim();

        const requestBody = {
            projectId,
            id: nodeId,
            nodes: getNodes().map((node) => {
                const nodeValue = node.id === nodeId
                    ? {
                        ...node.data.value,
                        chatMessage: sentText,
                        _chatThreadId: threadId,
                    }
                    : node.data.value;
                return {
                    id: node.id,
                    type: node.type,
                    position: node.position,
                    data: {
                        icon: node.data.icon,
                        background: node.data.background,
                        category: node.data.category,
                        subCategory: node.data.subCategory,
                        postions: node.data.postions,
                        currentCategory: node.data.currentCategory,
                        name: node.data.name,
                        index: node.data.index,
                        action: node.data.action,
                        value: nodeValue,
                        outputType: node.data.outputType,
                    }
                }
            }),
            edges: getEdges()
        };

        if (mode === 'admin') {
            const response = await fetch(`${webURL}/agents/adminAiAgent`, {
                method: 'POST',
                headers: {
                    'x-auth-token': userToken,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    ...requestBody,
                    adminMessage: sentText,
                })
            });

            if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
            const result = await response.json() as AgentRunResponse;
            if (result.workflowMeta) setWorkflowMeta(result.workflowMeta);
            return result;
        } else {
            const response = await fetch(`${webURL}/agents/startAiAgent`, {
                method: 'POST',
                headers: {
                    'x-auth-token': userToken,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(requestBody)
            });

            if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
            return await response.json() as AgentRunResponse;
        }
    };

    const loadChatHistory = React.useCallback(async (mode: ChatMode) => {
        if (!projectId || !userId || !userToken || !nodeIndexRef.current) return;

        try {
            const chatId = getThreadId(mode);
            if (!chatId) return;
            const response = await fetch(`${webURL}/accountChat/workflowChats/${chatId}`, {
                headers: {
                    'x-auth-token': userToken,
                    'Content-Type': 'application/json'
                }
            });

            if (response.ok) {
                const chatData = await response.json();
                if (chatData.messages) {
                    const currentMessages = mode === 'admin' ? adminMessages : userMessages;
                    
                    // Create formatted messages with unique IDs
                    const formattedMessages = chatData.messages.map((msg: any) => {
                        const uniqueId = `${msg.isAgent ? 'assistant' : 'user'}-${msg.body}-${new Date(msg.registration).getTime()}`;
                        return {
                            id: uniqueId,
                            role: msg.isAgent ? "assistant" : "user",
                            content: msg.body,
                            timestamp: new Date(msg.registration)
                        };
                    });

                    // Check if the new messages array has any truly unique content
                    const existingContent = new Set();
                    currentMessages.forEach(msg => {
                        existingContent.add(`${msg.role}-${msg.content}-${msg.timestamp.getTime()}`);
                    });
                    
                    const hasNewMessages = formattedMessages.some((newMsg: { id?: string, role: string, content: string, timestamp: Date }) => {
                        const msgKey = `${newMsg.role}-${newMsg.content}-${newMsg.timestamp.getTime()}`;
                        return !existingContent.has(msgKey);
                    });

                    if (hasNewMessages) {
                        if (mode === 'admin') setAdminMessages(formattedMessages);
                        else setUserMessages(formattedMessages);
                    }
                } else {
                    if (mode === 'admin') setAdminMessages([]);
                    else setUserMessages([]);
                }
            } else {
                if (mode === 'admin') setAdminMessages([]);
                else setUserMessages([]);
            }
        } catch (error) {
            console.error('Error loading chat history:', error);
        }
    }, [projectId, userId, userToken, getThreadId]);

    // When a workflow run completes, reload the USER chat to show new messages
    React.useEffect(() => {
        if (currentNode?.data?.output && currentNode?.data?.value?.memory === 'simple-memory') {
            loadChatHistory('user');
        }
    }, [currentNode?.data?.output, currentNode?.data?.value?.memory, loadChatHistory]);

    // Poll user chat periodically so input-node messages appear in real-time
    React.useEffect(() => {
        if (!projectId || !userId || !userToken || !nodeIndexRef.current) return;
        if (currentNode?.data?.value?.memory !== 'simple-memory') return;
        const interval = setInterval(() => loadChatHistory('user'), 5000);
        return () => clearInterval(interval);
    }, [projectId, userId, userToken, currentNode?.data?.value?.memory, loadChatHistory]);

    const formatIntermediateSteps = (steps: NonNullable<AgentRunResponse['intermediateSteps']>) => {
        return steps
            .map((step) => {
                const input = typeof step.input === 'string' ? step.input : JSON.stringify(step.input ?? {}, null, 2);
                const output = typeof step.output === 'string' ? step.output : JSON.stringify(step.output ?? {}, null, 2);
                const status = step.status || 'success';
                return [
                    `Step ${step.step}: ${step.tool}`,
                    `Status: ${status}`,
                    `Input: ${input}`,
                    status === 'error' ? `Error: ${step.error || 'Unknown error'}` : `Output: ${output}`,
                ].join('\n');
            })
            .join('\n\n');
    };

    const fetchVoiceClientSecret = React.useCallback(async () => {
        const response = await axios.post(`${webURL}/accountChat/realtime/client-secret`, {
            surface: 'ai-agent-chat',
        }, { headers: { 'x-auth-token': userToken || '' } });
        return response.data;
    }, [userToken]);

    const sendAgentTurn = React.useCallback(async (text: string, mode: ChatMode = chatMode): Promise<string> => {
        const sentText = text.trim();
        if (!sentText || !projectId || !userId || !userToken) return "";

        const userMessageId = `user-${sentText}-${Date.now()}`;
        const userMessage = {
            id: userMessageId,
            role: "user",
            content: sentText,
            timestamp: new Date()
        };

        let updatedMessages: Array<{ id?: string, role: string, content: string, timestamp: Date }> = [];
        setMessagesForMode(mode, (previous) => {
            updatedMessages = [...previous, userMessage];
            return updatedMessages;
        });
        setIsLoading(true);

        try {
            const result = await executeAiAgent(sentText, mode);

            if (result && result.message) {
                const nextMessages = [...updatedMessages];
                if (result.intermediateSteps?.length) {
                    const toolTraceMessageId = `tool-${formatIntermediateSteps(result.intermediateSteps).substring(0, 50)}-${Date.now()}`;
                    const toolTraceMessage = {
                        id: toolTraceMessageId,
                        role: "tool",
                        content: formatIntermediateSteps(result.intermediateSteps),
                        timestamp: new Date()
                    };
                    nextMessages.push(toolTraceMessage);
                }

                const aiMessageId = `assistant-${result.message.substring(0, 50)}-${Date.now()}`;
                const aiMessage = {
                    id: aiMessageId,
                    role: "assistant",
                    content: result.message,
                    timestamp: new Date()
                };
                setMessagesForMode(mode, [...nextMessages, aiMessage]);
                return result.message;
            } else {
                const errorMessageId = `error-no-response-${Date.now()}`;
                const errorMessage = {
                    id: errorMessageId,
                    role: "error",
                    content: "No response received from AI agent",
                    timestamp: new Date()
                };
                setMessagesForMode(mode, [...updatedMessages, errorMessage]);
                return "No response received from AI agent";
            }
        } catch (error) {
            console.error("Error sending message:", error);
            const errorMessageId = `error-failed-${Date.now()}`;
            const errorMessage = {
                id: errorMessageId,
                role: "error",
                content: "Failed to get response from AI agent",
                timestamp: new Date()
            };
            setMessagesForMode(mode, [...updatedMessages, errorMessage]);
            throw error;
        } finally {
            setIsLoading(false);
        }
    }, [chatMode, executeAiAgent, projectId, setMessagesForMode, userId, userToken]);

    const voice = useInteractiveVoiceAgent({
        fetchClientSecret: fetchVoiceClientSecret,
        onTurn: React.useCallback((text: string) => sendAgentTurn(text, 'user'), [sendAgentTurn]),
    });

    React.useEffect(() => {
        if (chatMode !== 'user') voice.end();
    }, [chatMode, voice.end]);

    React.useEffect(() => {
        return () => voice.end();
    }, [projectId, currentNode?.data?.index, voice.end]);

    const sendMessage = async () => {
        const sentText = inputMessage.trim();
        if (!sentText || !projectId || !userId || !userToken) return;
        setInputMessage("");

        if (chatMode === 'user' && voice.active) {
            const sentToVoice = voice.sendText(sentText);
            if (sentToVoice) return;
        }

        await sendAgentTurn(sentText, chatMode);
    };

    const handleKeyPress = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            sendMessage();
        }
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        const textarea = e.target;
        setInputMessage(textarea.value);
        textarea.style.height = 'auto';
        textarea.style.height = `${Math.min(textarea.scrollHeight, 150)}px`;
    };

    const clearChat = async () => {
        if (!projectId || !currentNode || !userToken) {
            setMessages([]);
            return;
        }

        const isMemoryEnabled = currentNode?.data?.value?.memory === 'simple-memory';

        try {
            const chatId = getThreadId(chatMode);
            if (chatId) {
                await axios.delete(`${webURL}/agents/aiAgentChat?thread_id=${encodeURIComponent(chatId)}`, {
                    headers: { 'x-auth-token': userToken }
                });
            }

            setMessages([]);

            if (isMemoryEnabled && chatId) {
                await axios.post(`${webURL}/agents/aiAgentChat`, {
                    thread_id: chatId,
                    projectId: projectId,
                    agentIndex: currentNode.data.index
                }, {
                    headers: { 'x-auth-token': userToken }
                });
            }
        } catch (error) {
            console.error('Error clearing chat:', error);
            setMessages([]);
        }
    };

    // Clear ALL agent memory — checkpoints + both admin & user chats
    const clearAllMemory = async () => {
        if (!projectId || !currentNode || !userToken) return;
        const nodeIndex = currentNode.data.index;
        if (!nodeIndex) return;
        setIsClearingMemory(true);
        try {
            await axios.delete(
                `${webURL}/agents/clearAllAgentMemory?projectId=${encodeURIComponent(projectId)}&nodeIndex=${encodeURIComponent(nodeIndex)}`,
                { headers: { 'x-auth-token': userToken } }
            );
            setUserMessages([]);
            setAdminMessages([]);
            hasLoadedUserHistory.current = false;
            hasLoadedAdminHistory.current = false;

            // Recreate both chat docs for memory persistence
            const isMemoryEnabled = currentNode?.data?.value?.memory === 'simple-memory';
            if (isMemoryEnabled) {
                const userThreadId = getThreadId('user');
                const adminThreadId = getThreadId('admin');
                if (userThreadId) {
                    await axios.post(`${webURL}/agents/aiAgentChat`, {
                        thread_id: userThreadId, projectId, agentIndex: nodeIndex
                    }, { headers: { 'x-auth-token': userToken } });
                }
                if (adminThreadId) {
                    await axios.post(`${webURL}/agents/aiAgentChat`, {
                        thread_id: adminThreadId, projectId, agentIndex: nodeIndex
                    }, { headers: { 'x-auth-token': userToken } });
                }
            }
        } catch (error) {
            console.error('Error clearing all memory:', error);
        } finally {
            setIsClearingMemory(false);
        }
    };

    // Force an admin instruction (persists to DB)
    const forceInstruction = async () => {
        if (!projectId || !currentNode || !userToken || !forceInput.trim()) return;
        try {
            const response = await axios.post(`${webURL}/agents/adminForceInstruction`, {
                projectId,
                nodeIndex: currentNode.data.index,
                instruction: forceInput.trim(),
                action: 'force'
            }, { headers: { 'x-auth-token': userToken } });

            if (response.data?.success) {
                setForcedInstruction(response.data.forcedInstruction);
                setForceInput("");
                setShowForcePanel(false);
                // Sync local node value so settings panel + useEffect stay consistent
                updateNodeValue(nodeId, setNodes, {
                    systemMessage: response.data.currentSystemMessage,
                    _adminForcedInstruction: response.data.forcedInstruction,
                });
            }
        } catch (error) {
            console.error('Error forcing instruction:', error);
        }
    };

    // Revert forced instruction back to original
    const revertInstruction = async () => {
        if (!projectId || !currentNode || !userToken) return;
        try {
            const response = await axios.post(`${webURL}/agents/adminForceInstruction`, {
                projectId,
                nodeIndex: currentNode.data.index,
                action: 'revert'
            }, { headers: { 'x-auth-token': userToken } });

            if (response.data?.success) {
                setForcedInstruction(null);
                // Clear forced instruction fields from local node data so useEffect doesn't re-set
                updateNodeValue(nodeId, setNodes, {
                    systemMessage: response.data.currentSystemMessage,
                    _adminForcedInstruction: undefined,
                });
            }
        } catch (error) {
            console.error('Error reverting instruction:', error);
        }
    };

    // Load forced instruction state on mount
    React.useEffect(() => {
        if (currentNode?.data?.value?._adminForcedInstruction) {
            setForcedInstruction(currentNode.data.value._adminForcedInstruction);
        }
    }, [currentNode?.data?.value?._adminForcedInstruction]);

    const formatMessageContent = (content: string) => {
        if (!content) return null;
        // Detect structured Media Intelligence JSON output
        const trimmed = content.trim();
        const jsonStart = trimmed.indexOf('{');
        if (jsonStart !== -1) {
            try {
                const obj = JSON.parse(trimmed.slice(jsonStart));
                if (obj?.type === 'image_generation' && obj?.success && obj?.image_url) {
                    return (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                            <span style={{ fontSize: 12, fontWeight: 700, color: '#7c3aed' }}>🖼 Image Generated</span>
                            <img
                                src={obj.image_url}
                                alt={obj.prompt || 'Generated image'}
                                style={{ maxWidth: '100%', borderRadius: 8, border: '1px solid #e0e0e0' }}
                            />
                            {obj.prompt && (
                                <span style={{ fontSize: 11, color: '#666' }}><b>Prompt:</b> {obj.prompt}</span>
                            )}
                            {obj.metadata && (
                                <span style={{ fontSize: 11, color: '#999' }}>
                                    Model: {obj.metadata.model} · Size: {obj.metadata.size}
                                </span>
                            )}
                            <a
                                href={obj.image_url}
                                download="generated-image.png"
                                target="_blank"
                                rel="noreferrer"
                                style={{ fontSize: 12, color: '#7c3aed', fontWeight: 600, textDecoration: 'none', border: '1px solid #7c3aed', borderRadius: 5, padding: '3px 10px', alignSelf: 'flex-start' }}
                            >
                                ↓ Download Image
                            </a>
                        </div>
                    );
                }
                if (obj?.type === 'image_analysis' && obj?.success) {
                    const text = obj.answer || obj.description || '';
                    return (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                            <span style={{ fontSize: 12, fontWeight: 700, color: '#7c3aed' }}>🔍 Image Analysis</span>
                            <span style={{ whiteSpace: 'pre-wrap' }}>{text}</span>
                        </div>
                    );
                }
                if (obj?.type === 'document_generation') {
                    const EXT_ICONS: Record<string, string> = { pptx: '🟦', docx: '🟩', xlsx: '🟨', pdf: '🟥' };
                    if (obj.success && obj.file_url) {
                        const metaParts: string[] = [];
                        if (obj.metadata?.slides != null) metaParts.push(`${obj.metadata.slides} slides`);
                        if (obj.metadata?.pages  != null) metaParts.push(`${obj.metadata.pages} pages`);
                        if (obj.metadata?.sheets != null) metaParts.push(`${obj.metadata.sheets} sheets`);
                        const fileUrl: string = obj.file_url;
                        const fileName: string = obj.file_name || `document.${obj.file_type || 'docx'}`;
                        return (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px', background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: 8 }}>
                                    <span style={{ fontSize: 22 }}>{EXT_ICONS[obj.file_type] || '📄'}</span>
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: 1, flex: 1, minWidth: 0 }}>
                                        <span style={{ fontSize: 12, fontWeight: 600, color: '#0369a1', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{fileName}</span>
                                        {metaParts.length > 0 && <span style={{ fontSize: 10, color: '#7dd3fc' }}>{metaParts.join(' · ')}</span>}
                                    </div>
                                    <span style={{ fontSize: 10, fontWeight: 700, color: '#0ea5e9', background: '#e0f2fe', borderRadius: 4, padding: '2px 6px', flexShrink: 0 }}>
                                        {(obj.file_type || '').toUpperCase()}
                                    </span>
                                </div>
                                <button
                                    onClick={() => {
                                        fetch(fileUrl).then(r => r.blob()).then(blob => {
                                            const url = URL.createObjectURL(blob);
                                            const a = document.createElement('a');
                                            a.href = url; a.download = fileName;
                                            document.body.appendChild(a); a.click();
                                            document.body.removeChild(a);
                                            setTimeout(() => URL.revokeObjectURL(url), 10000);
                                        });
                                    }}
                                    style={{
                                        fontSize: 12, color: '#fff', fontWeight: 600, cursor: 'pointer',
                                        background: '#0ea5e9', border: 'none', borderRadius: 6,
                                        padding: '6px 14px', alignSelf: 'flex-start'
                                    }}
                                >
                                    ↓ Download {(obj.file_type || '').toUpperCase()}
                                </button>
                            </div>
                        );
                    }
                    return (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                            <span style={{ fontSize: 12, fontWeight: 700, color: '#dc2626' }}>⚠ Document Generation Failed</span>
                            {obj.error && <span style={{ fontSize: 12, color: '#6b7280' }}>{obj.error}</span>}
                        </div>
                    );
                }
            } catch { /* not JSON, fall through */ }
        }
        return content
            .split('\n')
            .map((line, index) => (
                <React.Fragment key={index}>
                    {line}
                    {index < content.split('\n').length - 1 && <br />}
                </React.Fragment>
            ));
    };

    const adminQuickActions = [
        { label: "Workflow runs today?", icon: <Zap size={12} /> },
        { label: "Is the trigger working?", icon: <Terminal size={12} /> },
        { label: "Describe the workflow", icon: <Bot size={12} /> },
    ];

    if (chatMode === 'monitor') {
        return (
            <ChatContainer>
                <TabBar>
                    <Tab
                        $active={true}
                        $mode="monitor"
                        onClick={() => setChatMode('monitor')}
                    >
                        <MessagesSquare size={14} />
                        <span>User Chats</span>
                    </Tab>
                    <Tab
                        $active={false}
                        $mode="user"
                        onClick={() => { setChatMode('user'); setInputMessage(''); }}
                    >
                        <MessageCircle size={14} />
                        <span>Chat</span>
                    </Tab>
                    <Tab
                        $active={false}
                        $mode="admin"
                        onClick={() => { setChatMode('admin'); setInputMessage(''); }}
                    >
                        <Shield size={14} />
                        <span>Admin</span>
                    </Tab>
                </TabBar>
                <MonitorContent>
                    <UserChatsMonitor
                        projectId={projectId || ''}
                        userToken={userToken || ''}
                        nodeIndex={String(currentNode?.data?.index || nodeIndexRef.current || '')}
                    />
                </MonitorContent>
            </ChatContainer>
        );
    }

    return (
        <ChatContainer>
            {/* Mode Tabs */}
            <TabBar>
                <Tab
                    $active={false}
                    $mode="monitor"
                    onClick={() => { setChatMode('monitor'); setInputMessage(''); }}
                >
                    <MessagesSquare size={14} />
                    <span>User Chats</span>
                </Tab>
                <Tab
                    $active={chatMode === 'user'}
                    $mode="user"
                    onClick={() => { setChatMode('user'); setInputMessage(''); }}
                >
                    <MessageCircle size={14} />
                    <span>Chat</span>
                </Tab>
                <Tab
                    $active={chatMode === 'admin'}
                    $mode="admin"
                    onClick={() => { setChatMode('admin'); setInputMessage(''); }}
                >
                    <Shield size={14} />
                    <span>Admin</span>
                </Tab>
            </TabBar>

            {/* Header */}
            <ChatHeader $mode={chatMode}>
                <HeaderLeft>
                    <HeaderIcon $mode={chatMode}>
                        {chatMode === 'admin' ? <Shield size={16} /> : <MessageCircle size={16} />}
                    </HeaderIcon>
                    <HeaderInfo>
                        <h3>{chatMode === 'admin' ? 'Admin Console' : 'AI Agent Chat'}</h3>
                        <HeaderSubtitle>
                            {chatMode === 'admin'
                                ? 'Workflow management & control'
                                : 'Your private conversation'}
                        </HeaderSubtitle>
                    </HeaderInfo>
                </HeaderLeft>
                <HeaderActions>
                    {chatMode === 'admin' && (
                        <AdminActionButton
                            onClick={clearAllMemory}
                            disabled={isClearingMemory}
                            title="Reset all agent memory (both admin & user)"
                            $variant="danger"
                        >
                            {isClearingMemory ? <LoadingSpinner /> : <RotateCcw size={13} />}
                            <span>Reset Memory</span>
                        </AdminActionButton>
                    )}
                    {chatMode === 'admin' && messages.length > 0 && (
                        <ClearButton onClick={clearChat} title="Clear this chat">
                            <Trash2 size={14} />
                        </ClearButton>
                    )}
                </HeaderActions>
            </ChatHeader>

            {/* Admin Quick Stats Bar — always visible in admin mode */}
            {chatMode === 'admin' && (
                <StatsBar>
                    {workflowMeta ? (
                        <>
                            <StatTile>
                                <StatIconChip $accent="blue">
                                    <Zap size={13} />
                                </StatIconChip>
                                <StatTileBody>
                                    <StatValue>{workflowMeta.runsToday}</StatValue>
                                    <StatLabel>Today</StatLabel>
                                </StatTileBody>
                            </StatTile>
                            <StatTile>
                                <StatIconChip $accent="purple">
                                    <Activity size={13} />
                                </StatIconChip>
                                <StatTileBody>
                                    <StatValue>{workflowMeta.totalRuns}</StatValue>
                                    <StatLabel>Total Runs</StatLabel>
                                </StatTileBody>
                            </StatTile>
                            <StatTile>
                                <StatIconChip $accent="teal">
                                    <Layers size={13} />
                                </StatIconChip>
                                <StatTileBody>
                                    <StatValue>{workflowMeta.nodeCount}</StatValue>
                                    <StatLabel>Nodes</StatLabel>
                                </StatTileBody>
                            </StatTile>
                            <StatTile>
                                <StatIconChip $accent={workflowMeta.loopActive ? 'green' : 'slate'}>
                                    <Radio size={13} />
                                </StatIconChip>
                                <StatTileBody>
                                    <StatValue>
                                        <StatusDot $active={workflowMeta.loopActive} />
                                        {workflowMeta.loopActive ? 'Active' : 'Inactive'}
                                    </StatValue>
                                    <StatLabel>Loop Status</StatLabel>
                                </StatTileBody>
                            </StatTile>
                        </>
                    ) : (
                        <StatItem style={{ width: '100%' }}>
                            <StatLabel>Loading stats...</StatLabel>
                        </StatItem>
                    )}
                </StatsBar>
            )}

            {/* Admin Force Instruction Panel */}
            {chatMode === 'admin' && (
                <ForceInstructionBar $variant={forcedInstruction ? 'active' : showForcePanel ? 'editing' : 'idle'}>
                    {forcedInstruction ? (
                        <ForcedBanner>
                            <ForcedBannerText>
                                <AlertTriangle size={12} />
                                <span>Forced: {forcedInstruction.length > 60 ? forcedInstruction.slice(0, 60) + '...' : forcedInstruction}</span>
                            </ForcedBannerText>
                            <RevertButton onClick={revertInstruction}>
                                <RotateCcw size={11} />
                                Revert
                            </RevertButton>
                        </ForcedBanner>
                    ) : showForcePanel ? (
                        <ForceInputRow>
                            <ForceInputField
                                value={forceInput}
                                onChange={(e) => setForceInput(e.target.value)}
                                placeholder="New system instruction to force..."
                                onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); forceInstruction(); } }}
                            />
                            <ForceSubmitButton onClick={forceInstruction} disabled={!forceInput.trim()}>
                                Force
                            </ForceSubmitButton>
                            <ForceCancelButton onClick={() => { setShowForcePanel(false); setForceInput(""); }}>
                                Cancel
                            </ForceCancelButton>
                        </ForceInputRow>
                    ) : (
                        <ForceIdleRow>
                            <ForceToggleButton onClick={() => setShowForcePanel(true)}>
                                <Shield size={11} />
                                Force Instruction
                            </ForceToggleButton>
                            <ForceIdleHint>No override active — agent follows its default instructions</ForceIdleHint>
                        </ForceIdleRow>
                    )}
                </ForceInstructionBar>
            )}

            {/* Messages */}
            <ChatMessages>
                {messages.length === 0 ? (
                    <EmptyChat>
                        <EmptyIconWrap $mode={chatMode}>
                            {chatMode === 'admin' ? <Shield size={30} /> : <MessageCircle size={30} />}
                        </EmptyIconWrap>
                        <EmptyTitle>
                            {chatMode === 'admin'
                                ? 'Admin Console'
                                : 'Start a conversation'}
                        </EmptyTitle>
                        <EmptySubtitle>
                            {chatMode === 'admin'
                                ? 'Ask about workflow status, force instructions, or manage your agent'
                                : 'Chat privately with your AI agent — your conversation is separate from other users'}
                        </EmptySubtitle>
                        {chatMode === 'admin' && (
                            <QuickActions>
                                {adminQuickActions.map((action, index) => (
                                    <QuickActionButton
                                        key={index}
                                        onClick={() => {
                                            setInputMessage(action.label);
                                        }}
                                    >
                                        {action.icon}
                                        {action.label}
                                    </QuickActionButton>
                                ))}
                            </QuickActions>
                        )}
                    </EmptyChat>
                ) : (
                    messages.map((message) => (
                        <MessageBubble key={message.id || `${message.role}-${message.timestamp.getTime()}`} $role={message.role} $mode={chatMode}>
                            <MessageAvatar $role={message.role} $mode={chatMode}>
                                {message.role === 'user'
                                    ? <User size={14} />
                                    : message.role === 'assistant'
                                        ? <Bot size={14} />
                                        : message.role === 'tool'
                                            ? <Terminal size={14} />
                                            : '⚠️'}
                            </MessageAvatar>
                            <MessageContentWrapper>
                                <MessageContent $role={message.role} $mode={chatMode}>
                                    {formatMessageContent(message.content)}
                                </MessageContent>
                                <MessageTime $role={message.role}>
                                    {message.timestamp.toLocaleTimeString([], {
                                        hour: '2-digit',
                                        minute: '2-digit'
                                    })}
                                </MessageTime>
                            </MessageContentWrapper>
                        </MessageBubble>
                    ))
                )}
                {isLoading && (
                    <MessageBubble $role="assistant" $mode={chatMode}>
                        <MessageAvatar $role="assistant" $mode={chatMode}>
                            <Bot size={14} />
                        </MessageAvatar>
                        <MessageContentWrapper>
                            <TypingIndicator $mode={chatMode}>
                                <TypingDot $mode={chatMode} />
                                <TypingDot $mode={chatMode} />
                                <TypingDot $mode={chatMode} />
                            </TypingIndicator>
                        </MessageContentWrapper>
                    </MessageBubble>
                )}
                <div ref={messagesEndRef} />
            </ChatMessages>

            {/* Input */}
            <ChatInputContainer $mode={chatMode}>
                {chatMode === 'user' && voice.active && (
                    <VoiceStatusStrip>
                        <VoiceStatusLeft>
                            <VoiceLiveDot $active={!voice.muted} />
                            <span>{getVoiceStatusLabel(voice.status, voice.muted)}</span>
                            {voice.error && <VoiceErrorText>{voice.error}</VoiceErrorText>}
                        </VoiceStatusLeft>
                        <VoiceInlineActions>
                            <VoiceIconButton type="button" onClick={voice.toggleMute} title={voice.muted ? "Unmute voice" : "Mute voice"}>
                                {voice.muted ? <MicOff size={13} /> : <Mic size={13} />}
                            </VoiceIconButton>
                            <VoiceIconButton type="button" onClick={voice.interrupt} title="Interrupt voice response">
                                <Volume2 size={13} />
                            </VoiceIconButton>
                            <VoiceStopButton type="button" onClick={voice.end} title="Stop voice agent">
                                <PhoneOff size={13} />
                                <span>Stop</span>
                            </VoiceStopButton>
                        </VoiceInlineActions>
                    </VoiceStatusStrip>
                )}
                <ChatInput
                    value={inputMessage}
                    onChange={handleInputChange}
                    onKeyPress={handleKeyPress}
                    placeholder={chatMode === 'admin'
                        ? "Ask about workflow, give instructions..."
                        : "Type your message..."
                    }
                    disabled={isLoading}
                    rows={1}
                />
                {chatMode === 'user' && (
                    <VoiceRoundButton
                        type="button"
                        onClick={() => (voice.active ? voice.toggleMute() : voice.start())}
                        disabled={!projectId || !userToken || voice.status === 'connecting'}
                        $active={voice.active}
                        $muted={voice.muted}
                        title={voice.active ? (voice.muted ? "Unmute voice agent" : "Mute voice agent") : "Start voice agent"}
                    >
                        {voice.active && voice.muted ? <MicOff size={16} /> : <Mic size={16} />}
                    </VoiceRoundButton>
                )}
                <SendButton
                    onClick={sendMessage}
                    disabled={!inputMessage.trim() || isLoading}
                    $mode={chatMode}
                >
                    {isLoading ? (
                        <LoadingSpinner />
                    ) : (
                        <Send size={16} />
                    )}
                </SendButton>
            </ChatInputContainer>
        </ChatContainer>
    );
}

// ==================== Styled Components ====================

const fadeInUp = keyframes`
   from { opacity: 0; transform: translateY(10px); }
   to { opacity: 1; transform: translateY(0); }
`;

const typingBounce = keyframes`
   0%, 80%, 100% { transform: scale(0.8); opacity: 0.5; }
   40% { transform: scale(1); opacity: 1; }
`;

const spin = keyframes`
   0% { transform: rotate(0deg); }
   100% { transform: rotate(360deg); }
`;

const ChatContainer = styled.div`
   border-radius: 12px;
   background: #fff;
   border: 1px solid #e1e5e9;
    margin-bottom: 0;
   box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
   overflow: hidden;
   display: flex;
   flex-direction: column;
    box-sizing: border-box;
    width: 100%;
    height: 100%;
    min-height: 0;
    min-width: 0;
`;

const MonitorContent = styled.div`
    flex: 1;
    width: 100%;
    min-width: 0;
    min-height: 0;
    overflow: hidden;
`;

const TabBar = styled.div`
   display: flex;
   border-bottom: 1px solid #e5e7eb;
   background: #fafbfc;
   flex-shrink: 0;
`;

const Tab = styled.button<{ $active: boolean; $mode: string }>`
   flex: 1;
   display: flex;
   align-items: center;
   justify-content: center;
   gap: 6px;
   padding: 10px 0;
   border: none;
   background: none;
   cursor: pointer;
   font-size: 13px;
   font-weight: 500;
   transition: all 0.2s ease;
   position: relative;
   color: ${p => p.$active ? (p.$mode === 'admin' ? '#7c3aed' : '#2563eb') : '#6b7280'};

   &::after {
      content: '';
      position: absolute;
      bottom: 0;
      left: 16px;
      right: 16px;
      height: 2px;
      border-radius: 2px 2px 0 0;
      background: ${p => p.$active
        ? (p.$mode === 'admin' ? '#7c3aed' : '#2563eb')
        : 'transparent'};
      transition: background 0.2s ease;
   }

   &:hover {
      color: ${p => p.$mode === 'admin' ? '#7c3aed' : '#2563eb'};
      background: ${p => p.$mode === 'admin' ? 'rgba(124,58,237,0.04)' : 'rgba(37,99,235,0.04)'};
   }

   span { font-weight: ${p => p.$active ? 600 : 500}; }
`;

const ChatHeader = styled.div<{ $mode: string }>`
   display: flex;
   justify-content: space-between;
   align-items: center;
   padding: 12px 16px;
   border-bottom: 1px solid #f1f3f4;
   flex-shrink: 0;
   position: relative;
   z-index: 1;
   box-shadow: 0 2px 8px rgba(15, 23, 42, 0.03);
   background: ${p => p.$mode === 'admin'
    ? 'linear-gradient(135deg, #f5f3ff 0%, #ede9fe 100%)'
    : 'linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)'};
`;

const HeaderLeft = styled.div`
   display: flex;
   align-items: center;
   gap: 10px;
`;

const HeaderIcon = styled.div<{ $mode: string }>`
   width: 34px;
   height: 34px;
   border-radius: 10px;
   display: flex;
   align-items: center;
   justify-content: center;
   flex-shrink: 0;
   background: ${p => p.$mode === 'admin'
    ? 'linear-gradient(135deg, #8b5cf6, #7c3aed)'
    : 'linear-gradient(135deg, #3b82f6, #2563eb)'};
   box-shadow: 0 4px 10px ${p => p.$mode === 'admin' ? 'rgba(124, 58, 237, 0.32)' : 'rgba(37, 99, 235, 0.32)'};
   color: white;
`;

const HeaderInfo = styled.div`
   h3 {
      font-size: 14px;
      font-weight: 600;
      color: #1f2937;
      margin: 0;
   }
`;

const HeaderSubtitle = styled.div`
   font-size: 11px;
   color: #6b7280;
   margin-top: 1px;
`;

const ClearButton = styled.button`
   background: white;
   border: 1px solid #d1d5db;
   color: #6b7280;
   padding: 6px 8px;
   border-radius: 6px;
   cursor: pointer;
   transition: all 0.2s ease;
   display: flex;
   align-items: center;

   &:hover {
      background: #fef2f2;
      border-color: #fca5a5;
      color: #ef4444;
   }
`;

// Admin Stats Bar
const StatsBar = styled.div`
   display: flex;
   align-items: stretch;
   gap: 4px;
   padding: 8px 12px;
   background: #faf5ff;
   border-bottom: 1px solid #ede9fe;
   flex-shrink: 0;
`;

const StatItem = styled.div`
   display: flex;
   flex-direction: column;
   align-items: center;
   gap: 1px;
`;

const StatTile = styled.div`
   display: flex;
   align-items: center;
   gap: 8px;
   padding: 6px 10px;
   border-radius: 10px;
   flex: 1;
   min-width: 0;
   transition: background 0.15s ease;

   &:hover {
      background: rgba(124, 58, 237, 0.06);
   }
`;

const StatIconChip = styled.div<{ $accent: 'blue' | 'purple' | 'teal' | 'green' | 'slate' }>`
   width: 28px;
   height: 28px;
   border-radius: 8px;
   display: flex;
   align-items: center;
   justify-content: center;
   flex-shrink: 0;
   color: #ffffff;
   background: ${p => {
      switch (p.$accent) {
         case 'blue': return 'linear-gradient(135deg, #60a5fa, #2563eb)';
         case 'purple': return 'linear-gradient(135deg, #a78bfa, #7c3aed)';
         case 'teal': return 'linear-gradient(135deg, #2dd4bf, #0d9488)';
         case 'green': return 'linear-gradient(135deg, #4ade80, #16a34a)';
         default: return 'linear-gradient(135deg, #cbd5e1, #94a3b8)';
      }
   }};
`;

const StatTileBody = styled.div`
   display: flex;
   flex-direction: column;
   gap: 1px;
   min-width: 0;
`;

const StatValue = styled.div`
   display: inline-flex;
   align-items: center;
   gap: 5px;
   font-size: 14px;
   font-weight: 700;
   color: #5b21b6;
   white-space: nowrap;
`;

const StatLabel = styled.div`
   font-size: 10px;
   color: #7c3aed;
   text-transform: uppercase;
   letter-spacing: 0.5px;
   white-space: nowrap;
   overflow: hidden;
   text-overflow: ellipsis;
`;

const StatusDot = styled.div<{ $active: boolean }>`
   width: 8px;
   height: 8px;
   border-radius: 50%;
   flex-shrink: 0;
   background: ${p => p.$active ? '#22c55e' : '#9ca3af'};
   box-shadow: 0 0 0 2px ${p => p.$active ? 'rgba(34,197,94,0.2)' : 'rgba(156,163,175,0.2)'};
`;

const ChatMessages = styled.div`
   flex: 1;
   min-height: 120px;
   overflow-y: auto;
   padding: 16px;
   background: #fafbfc;

   &::-webkit-scrollbar { width: 6px; }
   &::-webkit-scrollbar-track { background: transparent; }
   &::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 3px; }
   &::-webkit-scrollbar-thumb:hover { background: #94a3b8; }
`;

const EmptyChat = styled.div`
   display: flex;
   flex-direction: column;
   align-items: center;
   justify-content: center;
   height: 100%;
   text-align: center;
   padding: 20px;
`;

const EmptyIconWrap = styled.div<{ $mode: string }>`
   width: 72px;
   height: 72px;
   border-radius: 20px;
   display: flex;
   align-items: center;
   justify-content: center;
   margin-bottom: 16px;
   background: ${p => p.$mode === 'admin'
      ? 'linear-gradient(135deg, rgba(124,58,237,0.12), rgba(124,58,237,0.02))'
      : 'linear-gradient(135deg, rgba(37,99,235,0.12), rgba(37,99,235,0.02))'};
   color: ${p => p.$mode === 'admin' ? '#8b5cf6' : '#3b82f6'};
`;

const EmptyTitle = styled.div`
   font-size: 15px;
   font-weight: 600;
   color: #374151;
   margin-bottom: 4px;
`;

const EmptySubtitle = styled.div`
   font-size: 12px;
   color: #9ca3af;
   max-width: 260px;
   line-height: 1.4;
`;

const QuickActions = styled.div`
   display: flex;
   flex-wrap: wrap;
   gap: 6px;
   margin-top: 16px;
   justify-content: center;
`;

const QuickActionButton = styled.button`
   display: flex;
   align-items: center;
   gap: 5px;
   padding: 7px 12px;
   border: 1px solid #ddd6fe;
   background: white;
   border-radius: 16px;
   font-size: 11px;
   font-weight: 500;
   color: #7c3aed;
   cursor: pointer;
   transition: all 0.18s ease;

   &:hover {
      background: #f5f3ff;
      border-color: #c4b5fd;
      transform: translateY(-1px);
      box-shadow: 0 4px 10px rgba(124, 58, 237, 0.15);
   }
`;

const MessageBubble = styled.div<{ $role: string; $mode: string }>`
   display: flex;
   align-items: flex-start;
   gap: 8px;
   margin-bottom: 12px;
   animation: ${fadeInUp} 0.3s ease-out;
   ${p => p.$role === 'user' ? 'flex-direction: row-reverse;' : ''}
`;

const MessageAvatar = styled.div<{ $role: string; $mode: string }>`
   width: 28px;
   height: 28px;
   border-radius: 50%;
   display: flex;
   align-items: center;
   justify-content: center;
   flex-shrink: 0;
   color: white;

   ${p => p.$role === 'user'
    ? `background: ${p.$mode === 'admin' ? '#7c3aed' : '#2563eb'};`
    : p.$role === 'tool'
        ? 'background: #0f766e;'
        : p.$role === 'error'
            ? 'background: #ef4444;'
            : `background: ${p.$mode === 'admin' ? '#5b21b6' : '#475569'};`
   }
`;

const MessageContentWrapper = styled.div`
   max-width: calc(100% - 50px);
   display: flex;
   flex-direction: column;
`;

const MessageContent = styled.div<{ $role: string; $mode: string }>`
   padding: 10px 14px;
   border-radius: 16px;
   font-size: 13px;
   line-height: 1.5;
   white-space: pre-wrap;
   word-wrap: break-word;

   ${p => p.$role === 'user' ? css`
      background: ${p.$mode === 'admin' ? '#7c3aed' : '#2563eb'};
      color: white;
      border-bottom-right-radius: 4px;
   ` : p.$role === 'tool' ? css`
      background: #ecfeff;
      color: #115e59;
      border: 1px solid #99f6e4;
      border-bottom-left-radius: 4px;
      font-family: monospace;
      font-size: 12px;
   ` : p.$role === 'error' ? css`
      background: #fef2f2;
      color: #dc2626;
      border: 1px solid #fecaca;
      border-bottom-left-radius: 4px;
   ` : css`
      background: white;
      color: #374151;
      border: 1px solid #e5e7eb;
      border-bottom-left-radius: 4px;
      box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
   `}
`;

const MessageTime = styled.div<{ $role: string }>`
   font-size: 10px;
   color: #9ca3af;
   margin-top: 3px;
   padding: 0 4px;
   text-align: ${p => p.$role === 'user' ? 'right' : 'left'};
`;

const TypingIndicator = styled.div<{ $mode: string }>`
   display: flex;
   align-items: center;
   gap: 4px;
   padding: 14px;
   background: white;
   border: 1px solid #e5e7eb;
   border-radius: 16px;
   border-bottom-left-radius: 4px;
`;

const TypingDot = styled.div<{ $mode: string }>`
   width: 7px;
   height: 7px;
   border-radius: 50%;
   background: ${p => p.$mode === 'admin' ? '#7c3aed' : '#9ca3af'};
   animation: ${typingBounce} 1.4s infinite ease-in-out;

   &:nth-child(1) { animation-delay: -0.32s; }
   &:nth-child(2) { animation-delay: -0.16s; }
   &:nth-child(3) { animation-delay: 0s; }
`;

const ChatInputContainer = styled.div<{ $mode: string }>`
   display: flex;
   flex-wrap: wrap;
   gap: 8px;
   padding: 10px 16px;
   border-top: 1px solid #f1f3f4;
   background: white;
   align-items: flex-end;
   flex-shrink: 0;
`;

const VoiceStatusStrip = styled.div`
   width: 100%;
   display: flex;
   align-items: center;
   justify-content: space-between;
   gap: 10px;
   padding: 7px 9px;
   border: 1px solid #bfdbfe;
   border-radius: 9px;
   background: #eff6ff;
`;

const VoiceStatusLeft = styled.div`
   display: flex;
   align-items: center;
   gap: 7px;
   min-width: 0;
   color: #1e3a8a;
   font-size: 11px;
   font-weight: 700;
`;

const VoiceLiveDot = styled.span<{ $active: boolean }>`
   width: 7px;
   height: 7px;
   border-radius: 50%;
   flex-shrink: 0;
   background: ${p => p.$active ? '#16a34a' : '#94a3b8'};
   box-shadow: 0 0 0 4px ${p => p.$active ? 'rgba(34,197,94,0.16)' : 'rgba(148,163,184,0.16)'};
`;

const VoiceErrorText = styled.span`
   color: #b91c1c;
   font-weight: 600;
   overflow: hidden;
   text-overflow: ellipsis;
   white-space: nowrap;
`;

const VoiceInlineActions = styled.div`
   display: inline-flex;
   align-items: center;
   gap: 6px;
   flex-shrink: 0;
`;

const VoiceIconButton = styled.button`
   width: 28px;
   height: 28px;
   border-radius: 7px;
   border: 1px solid #bfdbfe;
   background: #ffffff;
   color: #2563eb;
   display: inline-flex;
   align-items: center;
   justify-content: center;
   cursor: pointer;
   transition: all 0.16s ease;

   &:hover {
      background: #dbeafe;
      border-color: #93c5fd;
   }
`;

const VoiceStopButton = styled.button`
   height: 28px;
   border-radius: 7px;
   border: 1px solid #fecaca;
   background: #fee2e2;
   color: #b91c1c;
   display: inline-flex;
   align-items: center;
   justify-content: center;
   gap: 5px;
   padding: 0 9px;
   cursor: pointer;
   font-size: 11px;
   font-weight: 800;
   transition: all 0.16s ease;

   &:hover {
      background: #fecaca;
      border-color: #fca5a5;
   }
`;

const VoiceRoundButton = styled.button<{ $active: boolean; $muted: boolean }>`
   width: 40px;
   height: 40px;
   border-radius: 10px;
   border: 1px solid ${p => p.$active ? (p.$muted ? '#cbd5e1' : '#93c5fd') : '#d1d5db'};
   background: ${p => p.$active ? (p.$muted ? '#f8fafc' : '#eff6ff') : '#ffffff'};
   color: ${p => p.$active ? (p.$muted ? '#64748b' : '#2563eb') : '#475569'};
   display: inline-flex;
   align-items: center;
   justify-content: center;
   cursor: pointer;
   flex-shrink: 0;
   transition: all 0.18s ease;

   &:hover:not(:disabled) {
      border-color: #93c5fd;
      color: #2563eb;
      background: #eff6ff;
      transform: translateY(-1px);
   }

   &:disabled {
      opacity: 0.6;
      cursor: not-allowed;
      transform: none;
   }
`;

const ChatInput = styled.textarea`
   flex: 1;
   border: 1px solid #d1d5db;
   border-radius: 10px;
   padding: 10px 14px;
   font-size: 13px;
   resize: none;
   min-height: 40px;
   max-height: 120px;
   background: white;
   transition: all 0.2s ease;
   font-family: inherit;
   line-height: 1.5;

   &:focus {
      outline: none;
      border-color: #2563eb;
      box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.08);
   }

   &:disabled {
      background: #f9fafb;
      cursor: not-allowed;
   }

   &::placeholder { color: #9ca3af; }
`;

const SendButton = styled.button<{ $mode: string }>`
   background: ${p => p.$mode === 'admin' ? '#7c3aed' : '#2563eb'};
   color: white;
   border: none;
   border-radius: 10px;
   padding: 10px;
   width: 40px;
   height: 40px;
   display: flex;
   align-items: center;
   justify-content: center;
   cursor: pointer;
   transition: all 0.2s ease;
   flex-shrink: 0;

   &:hover:not(:disabled) {
      background: ${p => p.$mode === 'admin' ? '#6d28d9' : '#1d4ed8'};
      transform: translateY(-1px);
   }

   &:disabled {
      background: #d1d5db;
      cursor: not-allowed;
      transform: none;
   }
`;

const LoadingSpinner = styled.div`
   width: 16px;
   height: 16px;
   border: 2px solid transparent;
   border-top: 2px solid white;
   border-radius: 50%;
   animation: ${spin} 1s linear infinite;
`;

// New styled components for admin features

const HeaderActions = styled.div`
   display: flex;
   align-items: center;
   gap: 6px;
`;

const AdminActionButton = styled.button<{ $variant?: string }>`
   display: flex;
   align-items: center;
   gap: 5px;
   padding: 6px 12px;
   border-radius: 8px;
   font-size: 11px;
   font-weight: 600;
   cursor: pointer;
   transition: all 0.18s ease;
   border: 1.5px solid ${p => p.$variant === 'danger' ? '#fecaca' : '#d1d5db'};
   background: white;
   color: ${p => p.$variant === 'danger' ? '#dc2626' : '#6b7280'};

   &:hover:not(:disabled) {
      background: ${p => p.$variant === 'danger' ? '#fef2f2' : '#f9fafb'};
      border-color: ${p => p.$variant === 'danger' ? '#f87171' : '#9ca3af'};
      transform: translateY(-1px);
      box-shadow: 0 4px 10px ${p => p.$variant === 'danger' ? 'rgba(239, 68, 68, 0.15)' : 'rgba(15, 23, 42, 0.08)'};
   }

   &:active:not(:disabled) {
      transform: translateY(0);
   }

   &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
   }

   span { white-space: nowrap; }
`;

const ForceInstructionBar = styled.div<{ $variant: 'idle' | 'active' | 'editing' }>`
   padding: 7px 16px;
   min-height: 30px;
   display: flex;
   align-items: center;
   flex-shrink: 0;
   transition: background 0.2s ease, border-color 0.2s ease;

   ${p => p.$variant === 'idle' ? css`
      background: #f8fafc;
      border-bottom: 1px solid #eef2f7;
   ` : css`
      background: #fefce8;
      border-bottom: 1px solid #fef08a;
   `}
`;

const ForceIdleRow = styled.div`
   display: flex;
   align-items: center;
   justify-content: space-between;
   gap: 12px;
   width: 100%;
`;

const ForceIdleHint = styled.span`
   font-size: 10.5px;
   color: #94a3b8;
   white-space: nowrap;
   overflow: hidden;
   text-overflow: ellipsis;

   @media (max-width: 420px) {
      display: none;
   }
`;

const ForcedBanner = styled.div`
   display: flex;
   align-items: center;
   justify-content: space-between;
   width: 100%;
   gap: 8px;
`;

const ForcedBannerText = styled.div`
   display: flex;
   align-items: center;
   gap: 6px;
   font-size: 11px;
   color: #92400e;
   flex: 1;
   min-width: 0;

   span {
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
   }
`;

const RevertButton = styled.button`
   display: flex;
   align-items: center;
   gap: 3px;
   padding: 3px 8px;
   border: 1px solid #fbbf24;
   background: white;
   border-radius: 4px;
   font-size: 10px;
   color: #92400e;
   cursor: pointer;
   flex-shrink: 0;
   transition: all 0.2s ease;

   &:hover {
      background: #fffbeb;
      border-color: #f59e0b;
   }
`;

const ForceInputRow = styled.div`
   display: flex;
   align-items: center;
   gap: 6px;
   width: 100%;
`;

const ForceInputField = styled.input`
   flex: 1;
   border: 1px solid #fbbf24;
   border-radius: 4px;
   padding: 4px 8px;
   font-size: 11px;
   background: white;
   color: #1f2937;

   &:focus {
      outline: none;
      border-color: #f59e0b;
   }

   &::placeholder { color: #d1d5db; }
`;

const ForceSubmitButton = styled.button`
   padding: 4px 10px;
   border: none;
   background: #f59e0b;
   color: white;
   border-radius: 4px;
   font-size: 11px;
   font-weight: 600;
   cursor: pointer;
   flex-shrink: 0;

   &:hover:not(:disabled) { background: #d97706; }
   &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

const ForceCancelButton = styled.button`
   padding: 4px 8px;
   border: 1px solid #d1d5db;
   background: white;
   color: #6b7280;
   border-radius: 4px;
   font-size: 11px;
   cursor: pointer;
   flex-shrink: 0;

   &:hover { background: #f9fafb; }
`;

const ForceToggleButton = styled.button`
   display: flex;
   align-items: center;
   gap: 4px;
   padding: 4px 10px;
   border: 1px dashed #c4b5fd;
   background: #ffffff;
   border-radius: 6px;
   font-size: 11px;
   font-weight: 600;
   color: #7c3aed;
   cursor: pointer;
   transition: all 0.18s ease;
   flex-shrink: 0;

   &:hover {
      background: #f5f3ff;
      border-style: solid;
      border-color: #a78bfa;
      transform: translateY(-1px);
   }
`;
